import { QueryClient } from '@tanstack/react-query';

/**
 * 🔴 THE QUERY CLIENT — ERP Phase 1a increment 7, and the first line of this product that uses a
 * dependency it has carried since Phase 1.
 *
 * ## ⚠️ IT WAS IN `package.json` AND IMPORTED BY NOTHING
 *
 * §1a's Delivers line says *"TanStack Query adopted"*. `@tanstack/react-query` has been installed
 * since Phase 1 with **zero imports in `web/src`** — in the lockfile, in nobody's bundle, while
 * every screen hand-rolled its own `useEffect`, its own loading flag and its own error state.
 * 🔬 No Exit clause named it, which is how a dependency sits unused for a dozen phases looking
 * adopted.
 *
 * ## 🔴 WHAT IS ADOPTED HERE, AND WHAT IS DELIBERATELY NOT
 *
 * The **shared reads** move: the company context and the membership list, which the nav, the route
 * guard and the company picker all want and which were being fetched separately with a hand-rolled
 * promise cache written three days ago.
 *
 * ⚠️ **The list screens did not move then.** They go through `useCursorList`, which owns cursor
 * paging and the filter-changes-clears-the-cursor rule — migrating that is a change to every list in
 * the product, and putting it in the same commit as the provider would have made both unreviewable.
 *
 * 🔴 THEY MOVED IN DS-10d (scope §3.1). `useCursorList` is built on `useInfiniteQuery` against THIS
 * client, so every cursor list now shares its cache and its defaults below — except `staleTime`: a list
 * mounted again always asks its first page (`refetchOnMount: 'always'`), because nothing invalidates a
 * listing after a write. A listing's key is `['cursor-list', company, path, filters, limit, count]`
 * (`cursorListKey`, kept with the hook because this module is on every first load), and signing in or
 * out drops them all. react-query's `signal` goes to `api.get`, so a request superseded by a filter
 * change is ABORTED on the wire rather than ignored when it lands, and for a list that draws `busy`
 * (DocumentList) the previous listing's rows stay on screen (`keepPreviousData`) instead of blanking.
 */

/**
 * 🔬 EVERY DEFAULT HERE IS A DECISION, because the library's own defaults are tuned for a
 * content site and this is a ledger.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * 🔴 `retry: false`. The library retries three times by default, and for this product that is
       * wrong twice over: a 403 from `ModuleGuard` or a 400 from a missing company header is a
       * settled answer, and retrying it turns one honest refusal into four identical ones in the
       * server's log. ⚠️ `client.ts` already handles the one retry that IS correct — a 401 refreshes
       * the token exactly once, because presenting a rotated refresh token twice is what the server
       * treats as theft.
       */
      retry: false,

      /**
       * ⚠️ NO REFETCH ON WINDOW FOCUS. A bookkeeper alt-tabs between this and a spreadsheet all day;
       * refetching every time would multiply the load on a shop's own server for a page they are
       * already looking at. 🔬 The data that must be fresh is fetched when it is ASKED for, and the
       * data here — which modules a company has, which companies a person belongs to — changes on
       * the order of months.
       */
      refetchOnWindowFocus: false,

      /**
       * 🔬 A MINUTE. Long enough that a navigation does not re-ask for the nav's own gating, short
       * enough that turning a module on in Settings is visible without a reload.
       */
      staleTime: 60_000,
    },
  },
});

/**
 * 🔴 THE KEYS, IN ONE PLACE.
 *
 * ⚠️ A key typed at the call site is a cache that silently splits in two: one component asks for
 * `['context']` and another for `['company-context']`, both work, and they never share an answer or
 * an invalidation. The bug is invisible — everything renders — and shows up as a nav that does not
 * update when Settings does.
 */
export const queryKeys = {
  /** Scoped by company, because switching company is exactly when the answer changes. */
  companyContext: (companyId: string | null) => ['company-context', companyId] as const,
  memberships: () => ['memberships'] as const,
};
